import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getApprovedMessages } from '../utils/storage';
import { format } from 'date-fns';

const { FiHeart, FiClock, FiUser } = FiIcons;

const KindnessGallery = ({ limit = 6 }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const approvedMessages = await getApprovedMessages();
        setMessages(approvedMessages.slice(-limit).reverse());
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [limit]);

  if (loading) {
    return (
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="mt-12"
      >
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-heading mb-3">Recent Acts of Kindness</h3>
          <p className="text-text font-medium text-lg">Loading messages...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="message-card animate-pulse">
              <div className="message-card-header">
                <div className="h-6 w-6 bg-gray-300 rounded-full"></div>
                <div className="h-4 w-24 bg-gray-300 rounded"></div>
              </div>
              <div className="message-card-content">
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-3"></div>
                <div className="h-16 bg-gray-300 rounded"></div>
              </div>
              <div className="message-card-footer">
                <div className="h-4 w-32 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  if (messages.length === 0) {
    return (
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="mt-12"
      >
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-heading mb-3">Recent Acts of Kindness</h3>
          <p className="text-text font-medium text-lg">No messages yet. Be the first to spread kindness!</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.4, duration: 0.5 }}
      className="mt-12"
    >
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-heading mb-3">Recent Acts of Kindness</h3>
        <p className="text-text font-medium text-lg">Messages spreading love and positivity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {messages.map((message, index) => (
          <motion.div
            key={message.id}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 * index, duration: 0.5 }}
          >
            <Link
              to={`/message/${message.id}`}
              className="block hover:transform hover:scale-105 transition-all duration-300 focus-visible"
            >
              <div className="message-card">
                <div className="message-card-header">
                  <SafeIcon icon={FiHeart} className="heart-logo text-xl" />
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="text-sm text-text" />
                    <span className="text-sm font-bold text-text">
                      {format(new Date(message.timestamp), 'MMM d')}
                    </span>
                  </div>
                </div>
                <div className="message-card-content">
                  {message.title && (
                    <h4 className="font-bold text-heading mb-3 text-lg">{message.title}</h4>
                  )}
                  <p className="message-text">"{message.content}"</p>
                </div>
                <div className="message-card-footer">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUser} className="text-sm text-text" />
                    <span className="font-bold text-text">{message.isAnonymous ? 'Anonymous' : message.sender}</span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {limit < 12 && messages.length > 0 && (
        <div className="text-center mt-8">
          <Link
            to="/gallery"
            className="clay-button-purple px-8 py-4 inline-flex items-center space-x-3 font-bold text-text hover:transform hover:scale-105 transition-all duration-300 focus-visible"
          >
            <span className="text-lg">View All Kindness Messages</span>
          </Link>
        </div>
      )}
    </motion.div>
  );
};

export default KindnessGallery;